import { createReducer, on } from '@ngrx/store';

import { isNil } from '@gfe/core/utils/shared-utils';

import { ChildInfo } from '@core/models/plan.model';

import * as PlanActions from './plan.actions';
import { PlanState } from './plan.selectors';

export const initialPlanState: PlanState = {
  plan: undefined,
  deployStatusOverrides: {}
};

export const planReducer = createReducer(
  initialPlanState,

  on(PlanActions.fetchPlans, (state) => state),

  on(PlanActions.setPlan, (state, { plan }) => ({
    ...state,
    plan
  })),

  on(PlanActions.showGlobalDataOnly, (state, { entityAutoCompleteGlobalData }) => {
    const currentPlan = state.plan;
    if (isNil(currentPlan)) {
      return state;
    }
    return {
      ...state,
      plan: {
        ...currentPlan,
        entityAutoCompleteGlobalData
      }
    };
  }),

  on(PlanActions.optimizationUpdated, (state, { result }) => {
    const currentPlan = state.plan;

    if (isNil(currentPlan) || currentPlan.id !== result.planId) {
      return state;
    }

    const childrenInfo = currentPlan.childrenInfo ?? [];
    const childExists = childrenInfo.some((c) => c.jobId === result.jobId);

    const updatedChildren = childExists
      ? childrenInfo.map((c) =>
          c.jobId === result.jobId
            ? {
                ...c,
                jobStatus: result.status,
                childPlanId: result.outputPlanId,
                // Refreshed with the status, never carried over: the verdict belongs to the
                // status being reported, and the one from the plan fetch is now stale.
                isCancelable: result.isCancelable
              }
            : c
        )
      : // The API orders this list newest-first, so a job reported over the websocket has to
        // go in front. Appending would show it last until the next fetch reordered it.
        [
          {
            jobId: result.jobId,
            jobStatus: result.status,
            childPlanId: result.outputPlanId,
            isCancelable: result.isCancelable
          } as ChildInfo,
          ...childrenInfo
        ];

    return {
      ...state,
      plan: {
        ...currentPlan,
        childrenInfo: updatedChildren
      }
    };
  }),

  on(PlanActions.optimizationDeleted, (state, { jobId, planId }) => {
    const currentPlan = state.plan;

    // The event is broadcast to the whole data space, so most of them are about a plan this
    // client is not looking at. Without the id check every one of those would still rebuild
    // `childrenInfo` by identity and wake every plan selector.
    if (isNil(currentPlan) || currentPlan.id !== planId) {
      return state;
    }

    return {
      ...state,
      plan: {
        ...currentPlan,
        childrenInfo: (currentPlan.childrenInfo ?? []).filter((c) => c.jobId !== jobId)
      }
    };
  }),

  on(
    PlanActions.deployStatusUpdated,
    (
      state,
      {
        planId,
        configId,
        status,
        updatedAt,
        name,
        warnings,
        error,
        totalMessages,
        sentCount,
        failedCount
      }
    ) => {
      const key = `${planId}__${configId}`;
      const updatedOverrides = {
        ...state.deployStatusOverrides,
        [key]: { status, updatedAt }
      };

      const currentPlan = state.plan;
      if (isNil(currentPlan) || currentPlan.id !== planId) {
        return { ...state, deployStatusOverrides: updatedOverrides };
      }

      // Set (not merge) the detail fields so status transitions clear stale data:
      // PROCESSING (retry started) clears both; SUCCESS sets warnings & clears the
      // error; ERROR sets the error & clears warnings. Matches the WS field spec.
      const nextWarnings = warnings ?? [];
      const nextError = error ?? null;
      // The counts are the exception: they merge. They are cumulative for the
      // (plan, configuration) pair rather than a tally of one deploy, so an event that
      // carries none has no news about how much is out — which is not the same as zero.
      // A PROCESSING retry and an ERROR raised before the channel's send loop both omit
      // them, and both leave a configuration whose messages are already out. `null` is a
      // value here, not an absence: it is how a channel with no per-message notion
      // reports its total.
      const nextCounts = {
        ...(totalMessages !== undefined && { totalMessages }),
        ...(sentCount !== undefined && { sentCount }),
        ...(failedCount !== undefined && { failedCount })
      };
      const existing = currentPlan.deployStatus ?? [];
      const known = existing.some((ds) => ds.configId === configId);
      const eventDate = new Date(updatedAt);
      const updatedDeployStatus = known
        ? existing.map((ds) =>
            ds.configId === configId
              ? {
                  ...ds,
                  // Prefer the event's name (live channel name) but keep the
                  // existing one if the event didn't carry it.
                  name: name ?? ds.name,
                  status,
                  error: nextError,
                  warnings: nextWarnings,
                  ...nextCounts,
                  updatedAt: eventDate
                }
              : ds
          )
        : [
            ...existing,
            // Unknown config (freshly-added channel): surface it instead of dropping
            // it. Use the event's channel name; fall back to configId only when the
            // event didn't include a name (until the next plan refetch supplies one).
            {
              configId,
              name: name ?? configId,
              status,
              error: nextError,
              warnings: nextWarnings,
              ...nextCounts,
              createdAt: eventDate,
              updatedAt: eventDate
            }
          ];

      return {
        ...state,
        deployStatusOverrides: updatedOverrides,
        plan: { ...currentPlan, deployStatus: updatedDeployStatus }
      };
    }
  ),

  on(PlanActions.clearPlan, () => initialPlanState)
);
